import api from "../../service/api";
import { useEffect, useMemo, useState } from "react";
import {
  BarChart3,
  CalendarDays,
  Layers,
  Ticket,
  TrendingUp,
  UserPlus,
  Users,
  UserCircle2,
} from "lucide-react";
import EchartGraph from "../../components/Echarts/EchartGraph";
import type { EChartsOption } from "echarts-for-react";
import { formatMoney } from "../../utils/formatMoney";

export interface AdminMetricsPayload {
  year: number;
  revenueYearCents: number;
  monthlyRevenue: { month: number; monthLabel: string; revenueCents: number }[];
  topParticipantsByEvents: {
    id: string;
    name: string;
    document: string | null;
    eventsAttended: number;
  }[];
  topUsersByParticipantRegistrations: {
    id: string;
    name: string;
    email: string;
    participantRegistrationCount: number;
  }[];
  totalRegisteredUsers: number;
  totalParticipants: number;
  averageTicketPerParticipantYearCents: number;
  averageParticipantsPerEvent: number;
  totalEvents: number;
}

const CURRENT_YEAR = new Date().getFullYear();
const YEAR_OPTIONS = Array.from({ length: 6 }, (_, i) => CURRENT_YEAR - i);

export function AdminMetrics() {
  const [year, setYear] = useState(CURRENT_YEAR);
  const [data, setData] = useState<AdminMetricsPayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    api
      .get<AdminMetricsPayload>(`/metrics/admin`, { params: { year } })
      .then((res) => {
        if (!cancelled) setData(res.data);
      })
      .catch(() => {
        if (!cancelled)
          setError("Não foi possível carregar as métricas. Tente novamente.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [year]);

  const chartOption = useMemo<EChartsOption | null>(() => {
    if (!data) return null;
    return {
      tooltip: {
        trigger: "axis",
        formatter: (params: unknown) => {
          const items = params as { name: string; value: number }[];
          const p = items[0];
          if (!p) return "";
          return `${p.name}<br/>${formatMoney(p.value)}`;
        },
      },
      grid: {
        left: 12,
        right: 20,
        top: 28,
        bottom: 12,
        containLabel: true,
      },
      xAxis: {
        type: "category",
        data: data.monthlyRevenue.map((m) => m.monthLabel),
        axisLabel: { color: "#71717a" },
        axisTick: { alignWithLabel: true },
      },
      yAxis: {
        type: "value",
        splitNumber: 5,
        axisLabel: {
          color: "#71717a",
          margin: 10,
          hideOverlap: true,
          formatter: (v: number) => {
            const reais = v / 100;
            if (reais >= 1_000_000) {
              return `R$ ${(reais / 1_000_000)
                .toFixed(1)
                .replace(".", ",")} mi`;
            }
            if (reais >= 1_000) {
              return `R$ ${(reais / 1_000).toFixed(1).replace(".", ",")} mil`;
            }
            return `R$ ${reais.toLocaleString("pt-BR", {
              maximumFractionDigits: 0,
            })}`;
          },
        },
      },
      series: [
        {
          name: "Faturamento",
          type: "bar",
          data: data.monthlyRevenue.map((m) => m.revenueCents),
          itemStyle: {
            color: "#015832",
            borderRadius: [6, 6, 0, 0],
          },
        },
      ],
    };
  }, [data]);

  if (loading && !data) {
    return (
      <div
        className="ds-card space-y-4 p-8"
        role="status"
        aria-live="polite"
        aria-busy="true"
      >
        <div className="h-5 w-48 animate-pulse rounded-md bg-zinc-200 dark:bg-zinc-800" />
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div
              key={i}
              className="h-28 animate-pulse rounded-xl bg-zinc-100 dark:bg-zinc-800/80"
            />
          ))}
        </div>
        <p className="text-center text-sm text-zinc-500 dark:text-zinc-400">
          Carregando métricas do sistema…
        </p>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div
        className="rounded-xl border border-red-200/90 bg-red-50 p-6 text-sm text-red-800 shadow-sm dark:border-red-900/50 dark:bg-red-950/35 dark:text-red-200"
        role="alert"
      >
        {error ?? "Dados indisponíveis."}
      </div>
    );
  }

  return (
    <section className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h2 className="flex items-center gap-2 text-xl font-semibold tracking-tight text-zinc-900 dark:text-white">
            <BarChart3 className="h-6 w-6 text-apcap dark:text-emerald-400" />
            Métricas administrativas
          </h2>
          <p className="text-sm text-zinc-600 dark:text-zinc-400 mt-1">
            Faturamento por inscrições, totais e rankings de uso da plataforma.
          </p>
        </div>
        <label className="flex flex-col gap-1 text-sm">
          <span className="text-zinc-600 dark:text-zinc-400">Ano</span>
          <select
            value={year}
            onChange={(e) => setYear(Number(e.target.value))}
            className="min-w-[8rem] rounded-lg border border-zinc-200 bg-white px-3 py-2 text-sm font-medium text-zinc-900 shadow-sm outline-none transition hover:border-zinc-300 focus:border-apcap focus:ring-2 focus:ring-apcap/20 dark:border-zinc-600 dark:bg-zinc-900 dark:text-white dark:focus:border-emerald-500 dark:focus:ring-emerald-500/20"
          >
            {YEAR_OPTIONS.map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>
        </label>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <div className="ds-card p-5 dark:shadow-none">
          <div className="flex items-center justify-between">
            <p className="text-sm font-medium text-zinc-600 dark:text-zinc-400">
              Faturamento {data.year}
            </p>
            <div className="rounded-lg bg-apcap-muted p-2 dark:bg-apcap/20">
              <TrendingUp className="h-5 w-5 text-apcap dark:text-emerald-300" />
            </div>
          </div>
          <p className="mt-3 text-2xl font-bold text-zinc-900 dark:text-white tabular-nums">
            {formatMoney(data.revenueYearCents)}
          </p>
          <p className="text-xs text-zinc-500 dark:text-zinc-500 mt-1">
            Participantes cadastrados no ano × R$ 800,00 (valor fixo por
            ingresso).
          </p>
        </div>

        <div className="ds-card p-5 dark:shadow-none">
          <div className="flex items-center justify-between">
            <p className="text-sm font-medium text-zinc-600 dark:text-zinc-400">
              Usuários cadastrados
            </p>
            <div className="rounded-lg bg-blue-100 dark:bg-blue-900/30 p-2">
              <Users className="h-5 w-5 text-blue-600 dark:text-blue-400" />
            </div>
          </div>
          <p className="mt-3 text-2xl font-bold text-zinc-900 dark:text-white tabular-nums">
            {data.totalRegisteredUsers.toLocaleString("pt-BR")}
          </p>
        </div>

        <div className="ds-card p-5 dark:shadow-none">
          <div className="flex items-center justify-between">
            <p className="text-sm font-medium text-zinc-600 dark:text-zinc-400">
              Total de participantes
            </p>
            <div className="rounded-lg bg-emerald-100 dark:bg-emerald-900/30 p-2">
              <UserCircle2 className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
            </div>
          </div>
          <p className="mt-3 text-2xl font-bold text-zinc-900 dark:text-white tabular-nums">
            {data.totalParticipants.toLocaleString("pt-BR")}
          </p>
          <p className="text-xs text-zinc-500 dark:text-zinc-500 mt-1">
            Total de registros na tabela{" "}
            <span className="font-mono">participants</span>.
          </p>
        </div>

        <div className="ds-card p-5 dark:shadow-none">
          <div className="flex items-center justify-between">
            <p className="text-sm font-medium text-zinc-600 dark:text-zinc-400">
              Ticket médio ({data.year})
            </p>
            <div className="rounded-lg bg-rose-100 dark:bg-rose-900/30 p-2">
              <Ticket className="h-5 w-5 text-rose-600 dark:text-rose-400" />
            </div>
          </div>
          <p className="mt-3 text-2xl font-bold text-zinc-900 dark:text-white tabular-nums">
            {formatMoney(data.averageTicketPerParticipantYearCents)}
          </p>
          <p className="text-xs text-zinc-500 dark:text-zinc-500 mt-1">
            Faturamento de {data.year} ÷ participantes cadastrados em {data.year}.
          </p>
        </div>

        <div className="ds-card p-5 dark:shadow-none">
          <div className="flex items-center justify-between">
            <p className="text-sm font-medium text-zinc-600 dark:text-zinc-400">
              Média participantes / evento
            </p>
            <div className="rounded-lg bg-cyan-100 dark:bg-cyan-900/30 p-2">
              <Layers className="h-5 w-5 text-cyan-600 dark:text-cyan-400" />
            </div>
          </div>
          <p className="mt-3 text-2xl font-bold text-zinc-900 dark:text-white tabular-nums">
            {data.totalEvents > 0
              ? data.averageParticipantsPerEvent.toLocaleString("pt-BR", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })
              : "—"}
          </p>
          <p className="text-xs text-zinc-500 dark:text-zinc-500 mt-1">
            Total de participantes ({data.totalParticipants.toLocaleString("pt-BR")}) ÷{" "}
            {data.totalEvents.toLocaleString("pt-BR")} evento
            {data.totalEvents === 1 ? "" : "s"} cadastrado
            {data.totalEvents === 1 ? "" : "s"}.
          </p>
        </div>

        <div className="ds-card p-5 dark:shadow-none">
          <div className="flex items-center justify-between">
            <p className="text-sm font-medium text-zinc-600 dark:text-zinc-400">
              Média mensal ({data.year})
            </p>
            <div className="rounded-lg bg-amber-100 dark:bg-amber-900/30 p-2">
              <CalendarDays className="h-5 w-5 text-amber-600 dark:text-amber-400" />
            </div>
          </div>
          <p className="mt-3 text-2xl font-bold text-zinc-900 dark:text-white tabular-nums">
            {formatMoney(
              Math.round(data.revenueYearCents / 12) || 0
            )}
          </p>
        </div>
      </div>

      <div className="ds-card p-5 dark:shadow-none">
        <h3 className="mb-4 text-lg font-semibold tracking-tight text-zinc-900 dark:text-white">
          Faturamento mensal ({data.year})
        </h3>
        {chartOption ? (
          <div className="h-72 w-full">
            <EchartGraph options={chartOption} />
          </div>
        ) : null}
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <div className="ds-card overflow-hidden p-5 dark:shadow-none">
          <h3 className="mb-1 flex items-center gap-2 text-lg font-semibold tracking-tight text-zinc-900 dark:text-white">
            <UserPlus className="h-5 w-5 shrink-0 text-amber-500" />
            Mais cadastros de participantes ({data.year})
          </h3>
          <p className="text-xs text-zinc-500 dark:text-zinc-500 mb-4">
            Usuários que credenciaram mais participantes no ano (campo{" "}
            <span className="font-mono">user_id</span> em{" "}
            <span className="font-mono">participants</span>).
          </p>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-zinc-200 dark:border-zinc-700 text-left text-zinc-600 dark:text-zinc-400">
                  <th className="pb-2 pr-2 font-medium">#</th>
                  <th className="pb-2 pr-2 font-medium">Nome</th>
                  <th className="pb-2 pr-2 font-medium hidden sm:table-cell">
                    E-mail
                  </th>
                  <th className="pb-2 font-medium text-right">Participantes</th>
                </tr>
              </thead>
              <tbody>
                {data.topUsersByParticipantRegistrations.length === 0 ? (
                  <tr>
                    <td
                      colSpan={4}
                      className="py-6 text-center text-zinc-500 dark:text-zinc-500"
                    >
                      Nenhum participante cadastrado neste ano.
                    </td>
                  </tr>
                ) : (
                  data.topUsersByParticipantRegistrations.map((u, i) => (
                    <tr
                      key={u.id}
                      className="border-b border-zinc-100 dark:border-zinc-700/80 last:border-0"
                    >
                      <td className="py-2 pr-2 text-zinc-500">{i + 1}</td>
                      <td className="py-2 pr-2 text-zinc-900 dark:text-white font-medium">
                        {u.name}
                      </td>
                      <td className="py-2 pr-2 text-zinc-600 dark:text-zinc-400 hidden sm:table-cell truncate max-w-[12rem]">
                        {u.email}
                      </td>
                      <td className="py-2 text-right tabular-nums text-amber-600 dark:text-amber-400 font-semibold">
                        {u.participantRegistrationCount}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="ds-card overflow-hidden p-5 dark:shadow-none">
          <h3 className="mb-1 text-lg font-semibold tracking-tight text-zinc-900 dark:text-white">
            Participantes em mais eventos
          </h3>
          <p className="text-xs text-zinc-500 dark:text-zinc-500 mb-4">
            Eventos distintos com check-in registrado.
          </p>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-zinc-200 dark:border-zinc-700 text-left text-zinc-600 dark:text-zinc-400">
                  <th className="pb-2 pr-2 font-medium">#</th>
                  <th className="pb-2 pr-2 font-medium">Nome</th>
                  <th className="pb-2 pr-2 font-medium hidden sm:table-cell">
                    Documento
                  </th>
                  <th className="pb-2 font-medium text-right">Eventos</th>
                </tr>
              </thead>
              <tbody>
                {data.topParticipantsByEvents.length === 0 ? (
                  <tr>
                    <td
                      colSpan={4}
                      className="py-6 text-center text-zinc-500 dark:text-zinc-500"
                    >
                      Nenhum check-in encontrado.
                    </td>
                  </tr>
                ) : (
                  data.topParticipantsByEvents.map((p, i) => (
                    <tr
                      key={p.id}
                      className="border-b border-zinc-100 dark:border-zinc-700/80 last:border-0"
                    >
                      <td className="py-2 pr-2 text-zinc-500">{i + 1}</td>
                      <td className="py-2 pr-2 text-zinc-900 dark:text-white font-medium">
                        {p.name}
                      </td>
                      <td className="py-2 pr-2 text-zinc-600 dark:text-zinc-400 hidden sm:table-cell">
                        {p.document ?? "—"}
                      </td>
                      <td className="py-2 text-right tabular-nums text-emerald-600 dark:text-emerald-400 font-semibold">
                        {p.eventsAttended}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </section>
  );
}
